import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { FaBars, FaTimes, FaUserCircle, FaSignOutAlt, FaUser, FaAngleDown, FaPhone, FaTachometerAlt, FaTools, FaQuestionCircle } from "react-icons/fa";
import { auth, db } from "../firebase";
import { signOut } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import { calculateProfileCompletion } from "../utils/profileHelpers";
import "./Navbar.css";
import logo from "./image/PrrepX Logo.jpg";

const navItems = [
    { path: "/", label: "Home" },
    { path: "/tutorials", label: "Tutorials" },
    { path: "/doubt-portal24*7", label: "Doubt Portal" },
    { path: "/study-materials", label: "Study Materials" },
    { path: "/pyqs/yearwise", label: "Question Papers" },
    { path: "/after12", label: "Career After 12th" },
    { path: "/exam-guide", label: "JEE/NEET Guide"},
    /*
        type: "dropdown", label: "Student Support", items: [
            //{ path: "/confidential-support", label: "Confidential Support" },
            
            { path: "/community-page", label: "PrrepX Community" },
        ]
    },*/
    { path: "/meetourteam", label: "Our Team"},
    { path: "/about-prrepx", label: "About PrrepX" },
    { path: "/enquiry-now", label: "Enquiry Now!" },
];

const CallNowButton = ({ isMobile, handleLinkClick }) => (
    <a href="tel:+910000000000" className={isMobile ? 'mobile-call-now-btn' : 'call-now-btn'} onClick={handleLinkClick}>
        {!isMobile ? (
            <>
                <FaPhone className="call-icon" />
                <span className="call-text">Call Now</span>
                <span className="call-number">+919266517468</span>
            </>
        ) : (
            <>
                <FaPhone className="call-icon" />
                <div className="call-info">
                    <span className="call-text">Call Now</span>
                    <span className="call-number">+919266517468</span>
                </div>
            </>
        )}
    </a>
);

const EmployeePortalButton = ({ isMobile, handleLinkClick }) => (
    <Link to="https://employee.prrepxpathshala.live" target="_blank" rel="noopener noreferrer" className={isMobile ? 'mobile-btn' : 'employee-portal-btn'} onClick={handleLinkClick}>
        Employee Portal
    </Link>
);

const TestPortalButton = ({ isMobile, handleLinkClick }) => (
    <Link to="https://testseries.prrepxpathshala.live" target="_blank" rel="noopener noreferrer" className={isMobile ? 'mobile-btn' : 'test-portal-btn'} onClick={handleLinkClick}>
        Test Series Portal
    </Link>
);

const Navbar = () => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
    const [dropdowns, setDropdowns] = useState({});
    const [userDropdownOpen, setUserDropdownOpen] = useState(false);
    const [hasDashboardAccess, setHasDashboardAccess] = useState(false);
    const [profileCompletion, setProfileCompletion] = useState({ percentage: 100, isComplete: true });
    const navbarRef = useRef(null);

    const { currentUser: user, userData } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 1024);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (!user) {
            setHasDashboardAccess(false);
            return;
        }
        const accessRef = doc(db, 'dashboardAccess', user.uid);
        const unsubscribe = onSnapshot(accessRef, (docSnap) => {
            setHasDashboardAccess(docSnap.exists() && docSnap.data().hasAccess === true);
        });
        return () => unsubscribe();
    }, [user]);

    useEffect(() => {
        if (userData) {
            setProfileCompletion(calculateProfileCompletion(userData));
        }
    }, [userData]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (navbarRef.current && !navbarRef.current.contains(event.target)) {
                setDropdowns({});
                setUserDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleLogout = async () => {
        try {
            await signOut(auth);
            navigate("/");
            handleLinkClick();
        } catch (e) { console.error("Logout error:", e); }
    };

    const toggleDropdown = (label) => {
        setDropdowns(prev => prev[label] ? {} : { [label]: true });
        setUserDropdownOpen(false);
    };

    const toggleUserDropdown = () => setUserDropdownOpen(prev => !prev);

    const handleLinkClick = () => {
        setMenuOpen(false);
        setDropdowns({});
        setUserDropdownOpen(false);
    };

    // Shared Menu Content to keep Desktop & Mobile in sync
    const UserDropdownContent = () => (
        <>
            <li className="user-details-header">
                <div className="user-avatar">
                    {user?.photoURL ? <img src={user.photoURL} alt="Profile" className="user-avatar-img" /> : <FaUserCircle className="user-avatar-icon" />}
                </div>
                <div className="user-info">
                    <div className="user-display-name">{user?.displayName || "User"}</div>
                    <div className="user-email">{user?.email}</div>
                </div>
            </li>
            <li className="menu-divider"></li>
            
            <li>
                <Link to="/profile" onClick={handleLinkClick} className="user-menu-item">
                    <FaUser />
                    <span className={(!profileCompletion.isComplete && !isMobile) ? "view-profile-text-animated" : undefined}>View Profile</span>
                    {(!profileCompletion.isComplete && !isMobile) && (
                        <span className="view-profile-incomplete-tag">(Incomplete &middot; {100 - profileCompletion.percentage}%)</span>
                    )}
                </Link>
            </li>
            {(userData?.isAdmin || userData?.isSuperAdmin) && (
                <li><Link to="/user-management" onClick={handleLinkClick} className="user-menu-item"><FaTachometerAlt /> <span>User Management</span></Link></li>
            )}
            {/* {(userData?.isAdmin || userData?.isSuperAdmin) && (
                <li><Link to="/admin/disha" onClick={handleLinkClick} className="user-menu-item"><FaTools /> <span>Disha Chats (Admin)</span></Link></li>
            )} */}
            <li className="menu-divider"></li>
            <li className="logout-item" onClick={handleLogout}><FaSignOutAlt /> <span>Logout</span></li>
        </>
    );

    return (
        <nav className="new-navbar" ref={navbarRef}>
            <div className="navbar-container">
                <div className="top-bar">
                    <Link to="/" className="logo-box" onClick={handleLinkClick}>
                        <img src={logo} alt="PrepX Pathshala" className="logo-img" />
                        <div className="site-title">
                            <h1><span className="site-name">PrrepX</span> <span className="site-name-2">Pathshala</span></h1>
                            <span className="tagline">One Stop Solution for IIT-JEE/NEET & Class 6-12</span>
                        </div>
                    </Link>
                    <div className="right-actions">
                        <div className="desktop-actions">
                            <CallNowButton isMobile={false} handleLinkClick={handleLinkClick} />
                            {/* <TestPortalButton isMobile={false} handleLinkClick={handleLinkClick} /> */}

                            {!user ? (
                                <Link to="/login" className="login-btn" onClick={handleLinkClick}>Login</Link>
                            ) : (
                                <div className="user-dropdown-wrapper">
                                    <button className="desktop-user-toggle" onClick={toggleUserDropdown}>
                                        <FaUserCircle className="user-icon" />
                                        <div className="user-name-stack">
                                            <span className="user-name-display">{user.displayName?.split(" ")[0] || user.email?.split("@")[0]}</span>
                                            {!profileCompletion.isComplete && !isMobile && (
                                                <span className="profile-incomplete-chip">Profile Incomplete &middot; {100 - profileCompletion.percentage}%</span>
                                            )}
                                        </div>
                                        <FaAngleDown className={`dropdown-arrow ${userDropdownOpen ? "open" : ""}`} />
                                    </button>
                                    <ul className={`desktop-user-dropdown ${userDropdownOpen ? "open" : ""}`}>
                                        <UserDropdownContent />
                                    </ul>
                                </div>
                            )}
                        </div>
                        <div className="mobile-trigger-container">
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                {user && !profileCompletion.isComplete && isMobile && (
                                    <span className="mobile-menu-notification-badge">{100 - profileCompletion.percentage}%</span>
                                )}
                                <div className="menu-icon" onClick={() => setMenuOpen(!menuOpen)}>
                                    {menuOpen ? <FaTimes /> : <FaBars />}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="bottom-bar">
                    <ul className="nav-links-main">
                        {navItems.map((item) => (
                            item.type === "dropdown" ? (
                                <li key={item.label} className="dropdown">
                                    <span className="dropdown-toggle" onClick={() => toggleDropdown(item.label)}>
                                        {item.label} <FaAngleDown className={dropdowns[item.label] ? "open" : ""} />
                                    </span>
                                    <ul className={`dropdown-menu ${dropdowns[item.label] ? "active" : ""}`}>
                                        {item.items.map(sub => (<li key={sub.path}><Link to={sub.path} onClick={handleLinkClick}>{sub.label}</Link></li>))}
                                    </ul>
                                </li>
                            ) : (
                                <li key={item.path}>
                                    <Link to={item.path} className={location.pathname === item.path ? "active" : ""} onClick={handleLinkClick}>{item.label}</Link>
                                </li>
                            )
                        ))}
                    </ul>
                </div>
            </div>

            {/* World-class mobile dropdown overlay */}
            {isMobile && menuOpen && (
                <div className="mobile-dropdown-overlay">
                    <div className="mobile-dropdown-panel">
                        <div className="mobile-dropdown-header">
                            <Link to="/" className="logo-box" onClick={handleLinkClick}>
                                <img src={logo} alt="PrepX Pathshala" className="logo-img" />
                                <span className="site-name">PrrepX Pathshala</span>
                            </Link>
                            <button className="mobile-dropdown-close" onClick={() => setMenuOpen(false)}><FaTimes /></button>
                        </div>
                        <div className="mobile-dropdown-content">
                            <ul className="mobile-dropdown-list">
                                <li><CallNowButton isMobile={true} handleLinkClick={handleLinkClick} /></li>
                                {!user && (
                                    <li><Link to="/login" className="login-btn mobile-btn mobile-login-top" onClick={handleLinkClick}>Login</Link></li>
                                )}
                                {user && (
                                    <li className="mobile-user-li mobile-user-top">
                                        <button className="mobile-user-toggle" onClick={toggleUserDropdown}>
                                            <div className="toggle-left">
                                                <FaUserCircle className="user-icon" />
                                                <div className="toggle-name-stack">
                                                    <span>{user.displayName?.split(" ")[0] || user.email?.split("@")[0]}</span>
                                                    {!profileCompletion.isComplete && isMobile && (
                                                        <span className="mobile-incomplete-chip">Profile Incomplete &middot; {100 - profileCompletion.percentage}%</span>
                                                    )}
                                                </div>
                                            </div>
                                            <FaAngleDown className={userDropdownOpen ? "open" : ""} />
                                        </button>
                                        <ul className={`mobile-user-dropdown-list${userDropdownOpen ? " open" : ""}`}>
                                            <li>
                                                <Link to="/profile" onClick={handleLinkClick} className="user-menu-item">
                                                    <FaUser />
                                                    <span className={!profileCompletion.isComplete ? "view-profile-text-animated" : undefined}>View Profile</span>
                                                    {!profileCompletion.isComplete && (
                                                        <span className="view-profile-incomplete-tag">(Incomplete &middot; {100 - profileCompletion.percentage}%)</span>
                                                    )}
                                                </Link>
                                            </li>
                                            {(userData?.isAdmin || userData?.isSuperAdmin) && (
                                                <li><Link to="/user-management" onClick={handleLinkClick} className="user-menu-item"><FaTachometerAlt /> <span>User Management</span></Link></li>
                                            )}
                                            {/* {(userData?.isAdmin || userData?.isSuperAdmin) && (
                                                <li><Link to="/admin/disha" onClick={handleLinkClick} className="user-menu-item"><FaTools /> <span>Disha Chats (Admin)</span></Link></li>
                                            )} */}
                                            <li><span className="logout-item" onClick={handleLogout}><FaSignOutAlt /> Logout</span></li>
                                        </ul>
                                    </li>
                                )}
                                {navItems.map(item => (
                                    item.type === "dropdown" ? (
                                        <li key={item.label} className="mobile-dropdown">
                                            <details>
                                                <summary className="mobile-dropdown-toggle">{item.label} <FaAngleDown /></summary>
                                                <ul className="mobile-dropdown-menu">
                                                    {item.items.map(sub => (<li key={sub.path}><Link to={sub.path} onClick={handleLinkClick}>{sub.label}</Link></li>))}
                                                </ul>
                                            </details>
                                        </li>
                                    ) : <li key={item.path}><Link to={item.path} onClick={handleLinkClick}>{item.label}</Link></li>
                                ))}
                                {/* <li className="mobile-action-buttons">
                                    <TestPortalButton isMobile={true} handleLinkClick={handleLinkClick} />
                                </li> */}
                            </ul>
                        </div>
                    </div>
                </div>
            )}
        </nav>
    );
};

export default Navbar;